
import React, { useState } from 'react';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { User, Trash2 } from 'lucide-react';
import { useUpdateTicketStatus, useDeleteSupportTicket } from '@/hooks/useSupportTickets';
import { formatDate } from '@/utils/formatters';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { SupportTicket } from '@/types';
import AuthorQuickAccess from './AuthorQuickAccess';

interface TicketInformationProps {
  ticket: SupportTicket;
  author: any;
  status: string;
  setStatus: (status: string) => void;
  onTicketDeleted?: () => void;
}

const TicketInformation: React.FC<TicketInformationProps> = ({ 
  ticket, 
  author, 
  status, 
  setStatus,
  onTicketDeleted 
}) => {
  const updateStatusMutation = useUpdateTicketStatus();
  const deleteTicketMutation = useDeleteSupportTicket();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const handleStatusUpdate = async () => {
    if (!ticket.id || !status || status === ticket?.status) return;
    
    try {
      await updateStatusMutation.mutateAsync({
        ticketId: ticket.id,
        status: status as any
      });
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const handleDeleteTicket = async () => {
    if (!ticket.id) return;
    
    try {
      await deleteTicketMutation.mutateAsync(ticket.id);
      setShowDeleteDialog(false);
      if (onTicketDeleted) {
        onTicketDeleted();
      }
    } catch (error) {
      console.error('Error deleting ticket:', error);
    }
  };

  const canDeleteTicket = ticket.status === 'closed' || ticket.status === 'resolved';

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'low':
        return 'text-green-600';
      case 'medium':
        return 'text-yellow-600';
      case 'high':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  // Get initials for avatar
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase();
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Ticket Information</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <h3 className="text-sm font-medium text-gray-500 mb-1">Status</h3>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="resolved">Resolved</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div>
          <h3 className="text-sm font-medium text-gray-500 mb-1">Priority</h3>
          <div>
            <Badge className={`inline-flex items-center rounded-full px-3 py-1 capitalize ${getPriorityColor(ticket.priority)}`}>
              {ticket.priority}
            </Badge>
          </div>
        </div>
        
        <div>
          <h3 className="text-sm font-medium text-gray-500 mb-1">Category</h3>
          <p className="capitalize">{ticket.category}</p>
        </div>
        
        <div>
          <h3 className="text-sm font-medium text-gray-500 mb-1">Created Date</h3>
          <p>{formatDate(ticket.createdAt)}</p>
        </div>
        
        <div>
          <h3 className="text-sm font-medium text-gray-500 mb-1">Last Updated</h3>
          <p>{formatDate(ticket.updatedAt)}</p>
        </div>
        
        <Separator />
        
        <div>
          <h3 className="text-sm font-medium text-gray-500 mb-1">Author Information</h3>
          <div className="flex items-center gap-3 mt-2">
            <Avatar>
              <AvatarImage src={author?.avatar_url} />
              <AvatarFallback className="bg-emerald-600 text-white">
                {author?.name ? getInitials(author.name) : <User className="h-5 w-5" />}
              </AvatarFallback>
            </Avatar>
            <div>
              <p className="font-medium">{author?.name || 'Unknown'}</p>
              <p className="text-sm text-gray-500">{author?.email}</p>
            </div>
          </div>
        </div>

        <AuthorQuickAccess author={author} />
      </CardContent>
      <CardFooter className="flex justify-end gap-2">
        <Button 
          variant="outline"
          onClick={() => setStatus('closed')}
          disabled={status === 'closed'}
        >
          Close Ticket
        </Button>
        
        {canDeleteTicket && (
          <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
            <AlertDialogTrigger asChild>
              <Button 
                variant="destructive"
                disabled={deleteTicketMutation.isPending}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete Support Ticket</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to delete this support ticket? This action cannot be undone and will permanently remove the ticket and all its messages.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction 
                  onClick={handleDeleteTicket}
                  disabled={deleteTicketMutation.isPending}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  {deleteTicketMutation.isPending ? 'Deleting...' : 'Delete'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
        
        <Button 
          onClick={handleStatusUpdate}
          disabled={updateStatusMutation.isPending || status === ticket.status}
          className="bg-brand-500 hover:bg-brand-600"
        >
          {updateStatusMutation.isPending ? 'Updating...' : 'Update Status'}
        </Button>
      </CardFooter>
    </Card>
  );
};

export default TicketInformation;
